// src/contexts/ModalContext.js
import React, {
  createContext, useContext, useState, useEffect,
  useCallback, useMemo, useRef,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { FaBolt, FaGraduationCap, FaBriefcase, FaRocket, FaUsers, FaBrain, FaTrophy  } from 'react-icons/fa';
import DeckSpaceModal from '../components/shared/DeckSpaceModal';
import { loadFeedItems, loadFetchTimes } from '../components/pages/enrichFeedDb';
import { loadCareerFeedItems, loadCareerFetchTimes } from '../components/pages/careerFeedDb';
import { FaConnectdevelop } from "react-icons/fa6";
import {
  getPortfolioJobs,
  getCalendarEvents,
  getPathwayProgress, savePathwayProgress,
  getChosenTopSubject,
} from '../components/pages/dashboardDb';

// ── Extracted style constants ──────────────────────────────────────────────
const modalOverlayStyle = {
  position: 'fixed', inset: 0, background: 'rgba(0, 0, 0, 0.82)',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  zIndex: 2200, backdropFilter: 'blur(4px)',
};

const modalContentStyle = {
  background: 'linear-gradient(135deg, #0f1629bf, #1a2342c3)',
  color: 'white', padding: '28px 32px', borderRadius: '16px',
  width: 'min(95%, 1240px)', maxHeight: '94vh',
  overflowY: 'auto', overflowX: 'hidden',
  border: '1px solid rgba(54,167,248,0.5)',
  boxShadow: '0 15px 70px rgba(0,225,255,0.35)',
  position: 'relative',
};

const yearViewGridStyle = { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '20px' };
const monthViewGridStyle = { display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '10px', fontSize: '0.95rem' };
const dayHeaderStyle = { textAlign: 'center', fontWeight: 'bold', color: '#36a7f8', padding: '10px 0', background: 'rgba(54,167,248,0.12)', borderRadius: '8px' };
const emptyCellStyle = { padding: '10px' };

// Replace getCategoryColor's switch with this additional case
export function getCategoryColor(category) {
  switch (category?.toLowerCase()) {
    case 'intellectual pursuits': return { bg: '#00308a71',              border: '#2c76ff' };
    case 'clubs and societies':   return { bg: 'rgba(180,100,255,0.28)', border: '#b064ff' };
    case 'work experience':       return { bg: 'rgba(255,170,0,0.28)',   border: '#ffaa00' };
    case 'free course':           return { bg: 'rgba(0,204,153,0.28)',  border: '#00cc99' };  // Knowledge page — teal
    case 'ai skill course':       return { bg: 'rgba(93, 182, 255, 0.28)', border: '#5ddfff' };   // NEW — AIXcel — violet
    case 'job application':       return { bg: 'rgba(255,77,148,0.26)', border: '#ff4d94' };   // NEW — PortfolioX job tracker — pink
    default:                      return { bg: 'rgba(170,170,170,0.20)', border: '#aaaaaa' };
  }
}

export function getCategoryIcon(category) {
  const cat = category?.toLowerCase();
  if (['intellectual pursuits', 'clubs and societies', 'work experience'].includes(cat)) {
    return FaBolt;
  }
  if (cat === 'free course') {
    return FaGraduationCap;
  }
  if (cat === 'ai skill course') {
    return FaConnectdevelop; // NEW — distinguishes AIXcel courses at a glance
  }
  if (cat === 'job application') {
    return FaBriefcase; // NEW — PortfolioX job tracker deadlines/interviews
  }
  return null;
}

// Resolves a calendar-legend category (or an individual event's category)
// to its destination page/state. Shared by the clickable legend pills and
// can also replace SelectedEventPopup's inline branching later if desired.
export function getCategoryDestination(category, chosenTopSubject) {
  const cat = category?.toLowerCase();
  const superCurricularCats = ['intellectual pursuits', 'clubs and societies', 'work experience'];

  if (superCurricularCats.includes(cat)) {
    return {
      path: '/dashboard_enrich',
      state: {
        sidebarItem: 'supercurricular',
        preselectedSuperSubject: chosenTopSubject || undefined,
        preselectedCategory: category, // exact case, e.g. 'Intellectual Pursuits'
        fromCalendar: true,
      },
    };
  }
  if (cat === 'free course') {
    return {
      path: '/dashboard_enrich',
      state: {
        sidebarItem: 'knowledge',
        preselectedKnowledgeSubject: chosenTopSubject || undefined,
        preselectedKnowledgeCategory: 'Free Course', // NOTE: confirm this is the param dashboard_enrich.js reads for tab selection
        fromCalendar: true,
      },
    };
  }
  if (cat === 'ai skill course') {
    return {
      path: '/LeaderDashboard',
      state: {
        sidebarItem: 'aixcel',
        fromCalendar: true,
      },
    };
  }
  if (cat === 'job application') {
    return {
      path: '/CareerDashboard',
      state: {
        sidebarItem: 'portfoliox',
        openJobTracker: true,
        fromCalendar: true,
      },
    };
  }
  return null;
}

function getDaysInMonth(year, month)    { return new Date(year, month + 1, 0).getDate(); }
function getFirstDayOfMonth(year, month){ return new Date(year, month, 1).getDay(); }



/* ─── Context ────────────────────────────────────────────────────────────────*/
const ModalContext = createContext(null);

export function useModalContext() {
  const ctx = useContext(ModalContext);
  if (!ctx) throw new Error('useModalContext must be used within ModalProvider');
  return ctx;
}

/* ─── Provider ───────────────────────────────────────────────────────────────*/
export function ModalProvider({ userId, children }) {
  const navigate = useNavigate();

  const getEffectiveUserId = useCallback(() =>
    localStorage.getItem('viewingChildId') ||
    userId ||
    localStorage.getItem('userId') ||
    '',
  [userId]);

  /* modal open/close */
  const [openModalName, setOpenModalName] = useState(null);

  /* calendar state */
  const [calendarEvents, setCalendarEvents] = useState([]);
  const [chosenTopSubject, setChosenTopSubject] = useState(null);
  const today = useMemo(() => new Date(), []);
  const [currentMonth, setCurrentMonth] = useState(today.getMonth());
  const [currentYear,  setCurrentYear]  = useState(today.getFullYear());
  const [zoomedMonth,  setZoomedMonth]  = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [monthEventsModal, setMonthEventsModal] = useState(null);

const openMonthEvents = useCallback((month, events) => {
  setMonthEventsModal({ month, events });
}, []);
  const [pendingFreeCourseHighlight, setPendingFreeCourseHighlight] = useState(null);
  /* pathways state */
  const [pathwayProgress, setPathwayProgress] = useState({});

  const [selectedPathway, setSelectedPathway] = useState(null);

/* persist pathway progress to Dexie */
useEffect(() => {
  const uid = getEffectiveUserId();
  if (!uid || Object.keys(pathwayProgress).length === 0) return;

  const timeout = setTimeout(async () => {
    await savePathwayProgress(uid, pathwayProgress);
  }, 700); // debounce

  return () => clearTimeout(timeout);
}, [pathwayProgress, getEffectiveUserId]);

  /* open — loads fresh localStorage data every time */
  const openModal = useCallback(async (name) => {
  const uid = getEffectiveUserId();
  if (name === 'calendar') {
    try {
      // Read from Dexie (the authoritative source)
      const events = await getCalendarEvents(uid);
      setCalendarEvents(events);
    } catch {
      setCalendarEvents([]);
    }
    try {
      const subject = await getChosenTopSubject(uid);
      setChosenTopSubject(subject);
    } catch {
      setChosenTopSubject(null);
    }
    const now = new Date();
    setCurrentMonth(now.getMonth());
    setCurrentYear(now.getFullYear());
    setZoomedMonth(null);
    setSelectedEvent(null);
  }
if (name === 'pathways') {
  const savedProgress = await getPathwayProgress(uid);
  setPathwayProgress(savedProgress || {});
  
  const lastPathway = localStorage.getItem(`last_pathway_${uid}`) || null;
  setSelectedPathway(lastPathway);
}
  setOpenModalName(name);
}, [getEffectiveUserId]);

const closeModal = useCallback(() => {
    setOpenModalName(null);
    setSelectedPathway(null);
    setSelectedEvent(null);
    setZoomedMonth(null);
  }, []);

  /* Escape key closes whichever modal is currently open */
  useEffect(() => {
    if (!openModalName) return;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') closeModal();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [openModalName, closeModal]);

  /* navigate and close — preserves parent-view context from localStorage */
  const modalNavigate = useCallback((path, state = {}) => {
    const childId   = localStorage.getItem('viewingChildId');
    const childName = localStorage.getItem('viewingChildName') || '';
    if (childId) {
      navigate(
        { pathname: path, search: `?viewingChildId=${encodeURIComponent(childId)}&childName=${encodeURIComponent(childName)}` },
        { state: { ...state, viewingChildId: childId, childName } }
      );
    } else {
      navigate(path, { state });
    }
    closeModal();
  }, [navigate, closeModal]);
  
  const handleGoStep = useCallback((path, state, pathway) => {
  const uid = getEffectiveUserId();
  if (uid && pathway) localStorage.setItem(`last_pathway_${uid}`, pathway);
  modalNavigate(path, state || {});
}, [getEffectiveUserId, modalNavigate]);

const handleSwitchPathway = useCallback(() => {
  const uid = getEffectiveUserId();
  if (uid) localStorage.removeItem(`last_pathway_${uid}`);
  setSelectedPathway(null);
}, [getEffectiveUserId]);

  const deckSpacePrewarmed = useRef(false);
  const prewarmDeckSpace = useCallback(() => {
    if (deckSpacePrewarmed.current || !userId) return;
    deckSpacePrewarmed.current = true;
    Promise.all([
      loadFeedItems(userId),
      loadFetchTimes(userId),
      loadCareerFeedItems(userId),
      loadCareerFetchTimes(userId),
    ]).catch(() => {});
  }, [userId]);

 const value = useMemo(() => ({ openModal, closeModal, openModalName, prewarmDeckSpace }),
  [openModal, closeModal, openModalName, prewarmDeckSpace]);


  return (
    <ModalContext.Provider value={value}>
      {children}
{openModalName === 'calendar' && (
  <CalendarModal
    calendarEvents={calendarEvents}
    chosenTopSubject={chosenTopSubject}
    today={today}
    currentMonth={currentMonth} setCurrentMonth={setCurrentMonth}
    currentYear={currentYear}   setCurrentYear={setCurrentYear}
    zoomedMonth={zoomedMonth}   setZoomedMonth={setZoomedMonth}
    selectedEvent={selectedEvent} setSelectedEvent={setSelectedEvent}
    monthEventsModal={monthEventsModal}
    setMonthEventsModal={setMonthEventsModal}
    openMonthEvents={openMonthEvents}
    onClose={closeModal}
    onNavigate={modalNavigate}
  />
)}

{openModalName === 'pathways' && (
  <PathwaysModal
    pathwayProgress={pathwayProgress}
    setPathwayProgress={setPathwayProgress}
    selectedPathway={selectedPathway}
    setSelectedPathway={setSelectedPathway}
    onClose={closeModal}
    onNavigate={modalNavigate}
    onGoStep={handleGoStep}
    onSwitchPathway={handleSwitchPathway}
  />
)}

      {openModalName === 'deckspace' && (
      <DeckSpaceModal userId={userId} />
      )}

    </ModalContext.Provider>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   CALENDAR MODAL (extracted from Dashboard.js)
═══════════════════════════════════════════════════════════════════════════ */
const CalendarModal = React.memo(function CalendarModal({
  calendarEvents, chosenTopSubject, today,
  currentMonth, setCurrentMonth,
  currentYear,  setCurrentYear,
  zoomedMonth,  setZoomedMonth,
  selectedEvent, setSelectedEvent,
  monthEventsModal, setMonthEventsModal, openMonthEvents,
  onClose, onNavigate,
}) {
  const handlePrev = useCallback(() => {
    if (zoomedMonth === null) {
      setCurrentYear(y => y - 1);
    } else {
      const d = new Date(currentYear, zoomedMonth - 1, 1);
      setCurrentYear(d.getFullYear());
      setZoomedMonth(d.getMonth());
    }
  }, [zoomedMonth, currentYear, setCurrentYear, setZoomedMonth]);

  const handleNext = useCallback(() => {
    if (zoomedMonth === null) {
      setCurrentYear(y => y + 1);
    } else {
      const d = new Date(currentYear, zoomedMonth + 1, 1);
      setCurrentYear(d.getFullYear());
      setZoomedMonth(d.getMonth());
    }
  }, [zoomedMonth, currentYear, setCurrentYear, setZoomedMonth]);

const handleTitleClick = useCallback(() => {
    if (zoomedMonth !== null) setZoomedMonth(null);
  }, [zoomedMonth, setZoomedMonth]);

  const handleToday = useCallback(() => {
    setCurrentYear(today.getFullYear());
    setZoomedMonth(today.getMonth());
  }, [today, setCurrentYear, setZoomedMonth]);

  const isOnCurrentMonth = zoomedMonth === today.getMonth() && currentYear === today.getFullYear();

  return (
    <div
      className="modal-overlay"
      onClick={onClose}
      style={modalOverlayStyle}
    >
            <div
        onClick={e => e.stopPropagation()}
        style={modalContentStyle}
        className="invisible-scroll calendar-modal-content"
      >
        <button
          onClick={onClose}
          className="calendar-close-btn"
          style={{
            position: 'absolute', top: '8px', right: '8px',
            background: 'none', border: 'none', color: '#aaa',
            fontSize: '1.6rem', cursor: 'pointer', lineHeight: 1,
          }}
        >×</button>

      <button
            onClick={handleToday}
            disabled={isOnCurrentMonth}
            className='today-button'
            style={{
              marginLeft: '12px', padding: '8px 16px', borderRadius: '999px',
              background: isOnCurrentMonth ? 'rgba(0,225,255,0.08)' : 'rgba(0,225,255,0.18)',
              border: '1px solid rgba(54,167,248,0.5)',
              color: isOnCurrentMonth ? '#4a6b85' : '#00e1ff',
              fontSize: '0.85rem', fontWeight: 600,
              position: 'absolute', right: '110px', top: '50px',
              cursor: isOnCurrentMonth ? 'default' : 'pointer',
              transition: 'all 0.2s ease', whiteSpace: 'nowrap',
            }}
          >
            Today
          </button>
        {/* Header */}
        <div
          className="my-calendar-header"
          style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            marginBottom: '28px',
            borderBottom: '1px solid rgba(54,167,248,0.4)', paddingBottom: '16px',
          }}
        >
                 <button
            onClick={handlePrev}
            className="calendar-nav-arrow-left"
            style={{ background: 'none', border: 'none', color: '#36a7f8', fontSize: '36px', cursor: 'pointer' }}
            title={zoomedMonth === null ? 'Previous year' : 'Previous month'}
          >←</button>

          <h2
            onClick={handleTitleClick}
            className="year-view-title"
            style={{
              margin: 0, fontSize: '2.2rem',
              color: zoomedMonth === null ? '#00e1ff' : '#36a7f8',
              cursor: zoomedMonth !== null ? 'pointer' : 'default',
            }}
            title={zoomedMonth !== null ? 'Click to return to year view' : ''}
          >
            {zoomedMonth === null
              ? currentYear
              : new Date(currentYear, zoomedMonth).toLocaleString('default', { month: 'long', year: 'numeric' })}
          </h2>

<button
            onClick={handleNext}
            className="calendar-nav-arrow-right"
            style={{ background: 'none', border: 'none', color: '#36a7f8', fontSize: '36px', cursor: 'pointer' }}
            title={zoomedMonth === null ? 'Next year' : 'Next month'}
          >→</button>

    
        </div>


        {/* Category legend — clickable, routes to the relevant page */}
        <div className='legends' style={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap', gap: '10px', marginBottom: '20px' }}>
          {['Intellectual Pursuits', 'Clubs and Societies', 'Work Experience', 'Free Course', 'AI Skill Course', 'Job Application'].map(cat => {
            const color = getCategoryColor(cat);
            const Icon = getCategoryIcon(cat);
            const destination = getCategoryDestination(cat, chosenTopSubject);
            return (
              <button
                key={cat}
                onClick={() => destination && onNavigate(destination.path, destination.state)}
                title={destination ? `Go to ${cat}` : undefined}
                style={{
                  display: 'flex', alignItems: 'center', gap: '6px',
                  padding: '4px 10px', borderRadius: '999px',
                  background: color.bg, border: `1px solid ${color.border}`,
                  fontSize: '0.75rem', color: '#e0f7ff',
                  cursor: destination ? 'pointer' : 'default',
                  transition: 'transform 0.15s ease, filter 0.15s ease',
                }}
                onMouseEnter={e => { if (destination) { e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.filter = 'brightness(1.15)'; } }}
                onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.filter = 'brightness(1)'; }}
              >
                {Icon && <Icon style={{ fontSize: '0.85rem', color: color.border }} />}
                {cat}
              </button>
            );
          })}
        </div>

        {/* Year / Month view */}
        {zoomedMonth === null
?<YearView
  calendarEvents={calendarEvents}
  currentYear={currentYear}
  today={today}
  onZoom={setZoomedMonth}
  onSelectEvent={setSelectedEvent}
  onOpenMonthEvents={openMonthEvents}   // <-- add this line
/>
         : <MonthView calendarEvents={calendarEvents} currentYear={currentYear}  currentMonth={zoomedMonth} today={today} onSelectEvent={setSelectedEvent} />
        }

       {selectedEvent && (
  <SelectedEventPopup
    event={selectedEvent}
    onClose={() => setSelectedEvent(null)}
    onNavigate={onNavigate}
  />
)}

{/* Add this new block */}
{monthEventsModal && (
  <MonthEventsModal
    month={monthEventsModal.month}
    events={monthEventsModal.events}
    onClose={() => setMonthEventsModal(null)}
    onSelectEvent={(event) => {
      setSelectedEvent(event);
      setMonthEventsModal(null);
    }}
    onNavigate={onNavigate}
  />
)}
      </div>
    </div>
  );
});

const YearView = React.memo(function YearView({
  calendarEvents,
  currentYear,
  today,
  onZoom,
  onSelectEvent,
  onOpenMonthEvents, // new prop
}) {
  const monthEvents = useMemo(() => {
  const result = [];
  for (let month = 0; month < 12; month++) {
    const monthStart = new Date(currentYear, month, 1);
    const monthEnd = new Date(currentYear, month + 1, 0, 23, 59, 59, 999);

    const events = calendarEvents
      .filter((e) => {
        if (!e.startDate) return false;
        const s = new Date(e.startDate);
        const en = e.endDate ? new Date(e.endDate) : s;
        // Overlaps this month if it starts before/on month end AND ends after/on month start
        return s <= monthEnd && en >= monthStart;
      })
      .sort((a, b) => new Date(a.startDate) - new Date(b.startDate));

    result.push(events);
  }
  return result;
}, [calendarEvents, currentYear]);

  return (
    <div className="year-view" style={yearViewGridStyle}>
      {monthEvents.map((touchingEvents, month) => {
        const monthName = new Date(currentYear, month, 1).toLocaleString('default', { month: 'long' });
        const isCurrentMonth = month === today.getMonth() && currentYear === today.getFullYear();
        return (
          <div
            key={month}
            onClick={() => onZoom(month)}
            className="year-view-month"
            style={{
              padding: '20px',
              background: isCurrentMonth ? 'rgba(0,225,255,0.16)' : 'rgba(54,167,248,0.10)',
              border: isCurrentMonth ? '1.5px solid #00e1ff' : '1px solid rgba(54,167,248,0.35)',
              boxShadow: isCurrentMonth ? '0 0 0 3px rgba(0,225,255,0.12)' : 'none',
              borderRadius: '14px',
              cursor: 'pointer',
              transition: 'all 0.25s ease',
              minHeight: '250px',
              display: 'flex',
              flexDirection: 'column',
              position: 'relative',
              overflow: 'hidden',
            }}
            onMouseEnter={e => {
              e.currentTarget.style.background = 'rgba(54,167,248,0.22)';
              e.currentTarget.style.transform = 'translateY(-4px)';
              e.currentTarget.style.boxShadow = '0 12px 30px rgba(54,167,248,0.3)';
            }}
            onMouseLeave={e => {
              e.currentTarget.style.background = 'rgba(54,167,248,0.10)';
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = 'none';
            }}
          >
            <h4
              className="year-view-month-title"
              style={{
                margin: '0 0 16px 0',
                color: '#36a7f8',
                textAlign: 'center',
                fontSize: '1.4rem',
                zIndex: 10,
                position: 'relative',
              }}
            >
              {monthName}
            </h4>

            <div
              style={{
                position: 'absolute',
                top: '60px',
                left: '12px',
                right: '12px',
                bottom: '20px',
                pointerEvents: 'none',
              }}
            >
              {touchingEvents.map((event, idx) => {
                const color = getCategoryColor(event.category);
                const s = new Date(event.startDate);
                const en = event.endDate ? new Date(event.endDate) : s;
                return (
                  <div
                    key={event.id}
                    onClick={e => {
                      e.stopPropagation();
                      onSelectEvent(event);
                    }}
                    className="event-bar"
                    style={{
                      width: '100%',
                      position: 'absolute',
                      height: '25px',
                      background: color.bg,
                      borderLeft: `5px solid ${color.border}`,
                      borderRadius: '8px',
                      top: `${10 + (idx % 6) * 30}px`,
                      opacity: 0.92,
                      pointerEvents: 'auto',
                      cursor: 'pointer',
                      boxShadow: '0 3px 10px rgba(0,0,0,0.4)',
                      display: 'flex',
                      alignItems: 'center',
                      paddingLeft: '2px',
                      overflow: 'hidden',
                      transition: 'all 0.2s ease',
                      zIndex: 5,
                    }}
                    onMouseEnter={e => {
                      e.currentTarget.style.opacity = '1';
                      e.currentTarget.style.transform = 'scale(1.03)';
                      e.currentTarget.style.zIndex = '15';
                    }}
                    onMouseLeave={e => {
                      e.currentTarget.style.opacity = '0.92';
                      e.currentTarget.style.transform = 'scale(1)';
                      e.currentTarget.style.zIndex = '5';
                    }}
                    title={`${event.title} (${event.category})\n${event.startDate} → ${event.endDate || event.startDate}`}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%' }}>
                      {(() => {
                        const Icon = getCategoryIcon(event.category);
                        return Icon ? <Icon style={{ fontSize: '1.1rem', color: color.border, flexShrink: 0 }} /> : null;
                      })()}
                      <span
                        style={{
                          marginLeft: '-8px',
                          color: '#ffffff',
                          fontWeight: '600',
                          fontSize: '12px',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          textShadow: '0 1px 3px rgba(0,0,0,0.6)',
                        }}
                      >
                        {event.title}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>

            {touchingEvents.length === 0 && (
              <div className='calendar-no-events'
                style={{
                  flex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#666',
                  fontStyle: 'italic',
                  zIndex: 2,
                }}
              >
                No events this month
              </div>
            )}

            {/* Clickable "+X more" badge */}
            {touchingEvents.length > 6 && (
                          <div
                className="month-more-badge"
                onClick={e => {
                  e.stopPropagation();
                  onOpenMonthEvents(month, touchingEvents);
                }}
                style={{
                  position: 'absolute',
                  bottom: '8px',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  color: '#ccc',
                  fontSize: '0.85rem',
                  background: 'rgba(0,0,0,0.6)',
                  padding: '4px 12px',
                  borderRadius: '12px',
                  zIndex: 10,
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                }}
                onMouseEnter={e => {
                  e.currentTarget.style.background = 'rgba(54,167,248,0.4)';
                  e.currentTarget.style.color = '#fff';
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.background = 'rgba(0,0,0,0.6)';
                  e.currentTarget.style.color = '#ccc';
                }}
              >
                +{touchingEvents.length - 6} more
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
});

const MonthView = React.memo(function MonthView({ calendarEvents, currentYear, currentMonth, today, onSelectEvent }) {
  const daysData = useMemo(() => {
    const daysInMonth = getDaysInMonth(currentYear, currentMonth);
    const result = [];
    for (let day = 1; day <= daysInMonth; day++) {
      const dateKey = `${currentYear}-${String(currentMonth + 1).padStart(2,'0')}-${String(day).padStart(2,'0')}`;
      const thisDay = new Date(dateKey);
      const events = calendarEvents.filter(event => {
        if (!event.startDate) return false;
        const s = new Date(event.startDate);
        const en = event.endDate ? new Date(event.endDate) : s;
        return thisDay >= s && thisDay <= en;
      }).sort((a, b) => new Date(a.startDate) - new Date(b.startDate));
      result.push(events);
    }
    return result;
  }, [calendarEvents, currentYear, currentMonth]);

  return (
    <div className="month-view" style={monthViewGridStyle}>
         {['Sun','Mon','Tue','Wed','Thu','Fri','Sat'].map(day => (
        <div key={day} className="month-day-header" style={dayHeaderStyle}>{day}</div>
      ))}
      {Array(getFirstDayOfMonth(currentYear, currentMonth)).fill(null).map((_, i) => (
        <div key={`empty-${i}`} style={emptyCellStyle} />
      ))}
      {daysData.map((overlappingEvents, index) => {
        const dayNum = index + 1;
        const isToday = dayNum === today.getDate() && currentMonth === today.getMonth() && currentYear === today.getFullYear();
        // The rest of the JSX is identical; use `overlappingEvents` instead of the local filter
        return (
          <div
            key={dayNum} className="month-view-month"
            style={{
              minHeight: '160px', padding: '10px',
              background: isToday ? 'rgba(54,167,248,0.28)' : 'rgba(255,255,255,0.04)',
              border: isToday ? '2px solid #00e1ff' : '1px solid rgba(54,167,248,0.22)',
              borderRadius: '10px', position: 'relative', overflow: 'hidden',
            }}
          >
            <div className="day-number" style={{ fontWeight: isToday ? 'bold' : 'normal', color: isToday ? '#00e1ff' : '#ddd', marginBottom: '8px', fontSize: '1.2rem' }}>
              {dayNum}
            </div>
            {overlappingEvents.length > 0 && (
              <div style={{ position: 'relative', height: '100%', minHeight: '80px' }}>
                {overlappingEvents.map((event, idx) => {
                  const color = getCategoryColor(event.category);
                  return (
                    <div
                      key={event.id} onClick={() => onSelectEvent(event)} className="month-bar"
                      style={{
                        position: 'absolute', width: '100%', top: `${idx * 28}px`, height: '25px',
                        background: color.bg, borderLeft: `6px solid ${color.border}`,
                        borderRadius: '8px', display: 'flex', alignItems: 'center', padding: '0 2px',
                        color: '#ffffff', fontSize: '12px', fontWeight: '500',
                        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                        cursor: 'pointer', boxShadow: '0 2px 8px rgba(0,0,0,0.4)',
                        transition: 'all 0.18s', zIndex: 10 + idx,
                      }}
                      onMouseEnter={e => { e.currentTarget.style.transform = 'scale(1.04)'; e.currentTarget.style.zIndex = '30'; }}
                      onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)';    e.currentTarget.style.zIndex = `${10 + idx}`; }}
                      title={`${event.title} (${event.category})\n${event.startDate} → ${event.endDate || event.startDate}`}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%' }}>
                        {(() => {
                          const Icon = getCategoryIcon(event.category);
                          return Icon ? <Icon className="month-bar-icon" style={{ fontSize: '1.3rem', color: color.border, flexShrink: 0 }} /> : null;
                        })()}
                        <span style={{ marginLeft: '-8px', overflow: 'hidden', textOverflow: 'ellipsis' }}>{event.title}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
});

const SelectedEventPopup = React.memo(function SelectedEventPopup({ event, onClose, onNavigate }) {
  const color = getCategoryColor(event.category);
  const CategoryIcon = getCategoryIcon(event.category);
  const isSC = !!CategoryIcon;
  const eventStart = new Date(event.startDate);
  const eventEnd   = event.endDate ? new Date(event.endDate) : eventStart;

 const categoryLower = event.category?.toLowerCase();
  const isSuperCurricular = categoryLower === 'intellectual pursuits' ||
    categoryLower === 'clubs and societies' ||
    categoryLower === 'work experience';
  const isKnowledgeFreeCourse = categoryLower === 'free course';
  const isAiSkillCourse = categoryLower === 'ai skill course';
  const isJobApplication = categoryLower === 'job application';

  const handleTitleClick = (e) => {
    e.stopPropagation();
    if (isSuperCurricular) {
      onNavigate('/dashboard_enrich', {
        sidebarItem: 'supercurricular',
        preselectedSuperSubject: event.universitySubject || event.category,
        preselectedCategory: event.category,
        preselectedActivityId: event.supercurricular_id,
        scrollToActivity: true,
        fromCalendar: true,
      });
    } else if (isKnowledgeFreeCourse) {
      onNavigate('/dashboard_enrich', {
        sidebarItem: 'knowledge',
        preselectedKnowledgeSubject: event.universitySubject,
        preselectedFreeCourseTitle: event.title,
        fromCalendar: true,
      });
} else if (isAiSkillCourse) {
  onNavigate('/LeaderDashboard', {
    sidebarItem: 'aixcel',
    preselectedFacet: event.facet,
    preselectedCourseType: event.courseType,
    preselectedFreeCourseTitle: event.title,
    fromCalendar: true,
  });
} else if (isJobApplication) {
  onNavigate('/CareerDashboard', {
    sidebarItem: 'portfoliox',
    openJobTracker: true,
    fromCalendar: true,
  });
}
  };

  const isClickable = isSuperCurricular || isKnowledgeFreeCourse || isAiSkillCourse || isJobApplication;

  return (
      <div className="event-popup-overlay" style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2300 }} onClick={onClose}>
      <div
        onClick={e => e.stopPropagation()}
        style={{ background: '#0f1629', padding: '2.4rem 2.8rem', borderRadius: '16px', maxWidth: '720px', width: '92%', maxHeight: '90vh', overflowY: 'auto', border: `2px solid ${color.border}`, boxShadow: '0 15px 70px rgba(0,0,0,0.65)' }}
        className="custom-scrollbar event-popup-card"
      >
        <h3 style={{ margin: '0 0 1.4rem', color: color.border, fontSize: '1.8rem', display: 'flex', alignItems: 'center', gap: '12px' }}>
          {CategoryIcon && <CategoryIcon style={{ fontSize: '1.7rem', color: color.border }} />}
          {isClickable ? (
            <span
              onClick={handleTitleClick}
              style={{ cursor: 'pointer', color: '#40c4ff', transition: 'all 0.18s ease' }}
              onMouseEnter={e => { e.currentTarget.style.color = '#ffffff'; }}
              onMouseLeave={e => { e.currentTarget.style.color = '#40c4ff'; }}
              title={
                isSuperCurricular ? 'Click to view this activity in SuperCurricular tab'
                : isKnowledgeFreeCourse ? 'Click to view this course in Knowledge → Free Courses'
                : 'Click to view this course in AIXcel'
              }
            >{event.title}</span>
          ) : <span>{event.title}</span>}
        </h3>


        <div style={{ marginBottom: '1.8rem', lineHeight: 1.6, fontSize: '0.98rem' }}>
          <div style={{ color: '#ccc', marginBottom: '6px' }}>{event.category}</div>
          <div style={{ color: 'grey', fontSize: '0.9rem' }}>
            {eventStart.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
            {eventEnd.getTime() !== eventStart.getTime() && (
              <> → {eventEnd.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}</>
            )}
          </div>
        </div>

        {event.selectedExamples?.length > 0 && (
          <div style={{ margin: '1.8rem 0', padding: '1.2rem', background: 'rgba(255,170,0,0.08)', borderRadius: '12px', border: '1px solid rgba(255,170,0,0.25)' }}>
            <strong style={{ color: '#ffca28', display: 'block', marginBottom: '0.9rem' }}>Selected Activities</strong>
            <ul style={{ margin: 0, paddingLeft: '1.5rem', listStyle: 'disc', color: '#e0f7ff' }}>
              {event.selectedExamples.map((ex, i) => <li key={i} style={{ marginBottom: '0.5rem' }}>{ex}</li>)}
            </ul>
          </div>
        )}
      {event.description?.trim() && (
          <div style={{ padding: '1.2rem', background: 'rgba(54,167,248,0.08)', borderRadius: '12px', border: '1px solid rgba(54,167,248,0.25)', lineHeight: 1.6 }}>
            <strong style={{ color: '#40c4ff', display: 'block', marginBottom: '0.8rem' }}>
              {isJobApplication ? 'Company / Location' : 'Your Notes'}
            </strong>
            {event.description}
          </div>
        )}
        {isJobApplication && event.link && (
          <div style={{ marginTop: '1rem' }}>
            <a href={event.link} target="_blank" rel="noopener noreferrer" style={{ color: '#00e1ff' }}>
              View job posting ↗
            </a>
          </div>
        )}
           <div className="event-popup-footer" style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '2.2rem' }}>
          <button onClick={onClose} style={{ padding: '0.8rem 1.8rem', background: '#444', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer' }}>Close</button>
        </div>
      </div>
    </div>
  );
});

const MonthEventsModal = React.memo(function MonthEventsModal({
  month,
  events,
  onClose,
  onSelectEvent,
  onNavigate,
}) {
  const monthName = new Date(2025, month, 1).toLocaleString('default', { month: 'long' });

  return (
    <div
      className="month-events-overlay"
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.85)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 2400,
      }}
      onClick={onClose}
    >
      <div
        onClick={e => e.stopPropagation()}
        className="month-events-card"
        style={{
          background: '#0f1629',
          padding: '2rem 2.4rem',
          borderRadius: '16px',
          maxWidth: '700px',
          width: '92%',
          maxHeight: '85vh',
          display: 'flex',
          flexDirection: 'column',
          border: '1px solid rgba(54,167,248,0.4)',
          boxShadow: '0 20px 80px rgba(0,0,0,0.7)',
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '1.5rem',
          }}
        >
          <h3 style={{ margin: 0, color: '#36a7f8', fontSize: '1.6rem' }}>
            Events in {monthName}
            <span style={{ fontSize: '1rem', color: '#aaa', marginLeft: '12px' }}>
              ({events.length})
            </span>
          </h3>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              color: '#aaa',
              fontSize: '28px',
              cursor: 'pointer',
            }}
          >
            ×
          </button>
        </div>

        <div
          style={{ overflowY: 'auto', flex: 1, paddingRight: '8px' }}
          className="custom-scrollbar"
        >
          {events.map(event => {
            const color = getCategoryColor(event.category);
            const Icon = getCategoryIcon(event.category);
            const s = new Date(event.startDate);
            const e = event.endDate ? new Date(event.endDate) : s;
            return (
                    <div
                key={event.id}
                className="month-events-item"
                onClick={() => onSelectEvent(event)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '14px',
                  padding: '12px 16px',
                  marginBottom: '8px',
                  background: 'rgba(255,255,255,0.04)',
                  borderRadius: '10px',
                  border: `1px solid ${color.border}33`,
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                }}
                onMouseEnter={e => {
                  e.currentTarget.style.background = 'rgba(54,167,248,0.12)';
                  e.currentTarget.style.transform = 'translateX(4px)';
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.background = 'rgba(255,255,255,0.04)';
                  e.currentTarget.style.transform = 'translateX(0)';
                }}
              >
                {Icon && <Icon style={{ fontSize: '1.4rem', color: color.border, flexShrink: 0 }} />}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600, color: '#fff', fontSize: '1rem' }}>{event.title}</div>
                  <div style={{ fontSize: '0.8rem', color: '#aaa' }}>
                    {s.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
                    {e.getTime() !== s.getTime() &&
                      ` → ${e.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}`}
                    {' • '}
                    <span style={{ color: color.border }}>{event.category}</span>
                  </div>
                </div>
                <div
                  style={{
                    fontSize: '0.7rem',
                    color: '#888',
                    background: 'rgba(255,255,255,0.06)',
                    padding: '2px 10px',
                    borderRadius: '999px',
                    whiteSpace: 'nowrap',
                  }}
                >
                  View
                </div>
              </div>
            );
          })}
        </div>

        <div style={{ marginTop: '1.5rem', textAlign: 'right' }}>
          <button
            onClick={onClose}
            style={{
              padding: '0.6rem 1.8rem',
              background: '#444',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
            }}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
});

/* ═══════════════════════════════════════════════════════════════════════════
   PATHWAYS MODAL (extracted from Dashboard.js)
═══════════════════════════════════════════════════════════════════════════ */
const PathwaysModal = React.memo(function PathwaysModal({ pathwayProgress, setPathwayProgress, selectedPathway, setSelectedPathway, onClose, onNavigate, onGoStep, onSwitchPathway }) {
  if (!selectedPathway) {
    return <PathwaysChooser setSelectedPathway={setSelectedPathway} onClose={onClose} />;
  }
  return (
  <PathwayDetailModal
      selectedPathway={selectedPathway}
      pathwayProgress={pathwayProgress}
      setPathwayProgress={setPathwayProgress}
      setSelectedPathway={setSelectedPathway}
      onClose={onClose}
      onNavigate={onNavigate}
      onGoStep={onGoStep}
      onSwitchPathway={onSwitchPathway}
    />
  );
});

function PathwaysChooser({ setSelectedPathway, onClose }) {
  const btn = (accent, hover) => ({
    padding: '24px 28px', background: `rgba(${accent},0.10)`,
    border: `1px solid rgba(${accent},0.45)`, borderRadius: '14px',
    textAlign: 'left', cursor: 'pointer', transition: 'all 0.22s',
  });
  const hoverIn  = (e, accent) => { e.currentTarget.style.background = `rgba(${accent},0.22)`; e.currentTarget.style.transform = 'translateY(-3px)'; };
  const hoverOut = (e, accent) => { e.currentTarget.style.background = `rgba(${accent},0.10)`; e.currentTarget.style.transform = 'translateY(0)'; };

  const badge = (color, text) => (
    <span className="pathway-step-count" style={{ padding: '2px 10px', borderRadius: '999px', fontSize: '0.75rem', background: `rgba(${color},0.15)`, border: `1px solid #${color.split(',').map(n=>parseInt(n).toString(16).padStart(2,'0')).join('')}`, color: `rgb(${color})` }}>{text}</span>
  );

  return (
    <div className="modal" style={{ position: 'fixed', inset: 0, background: 'rgba(0, 0, 0, 0.79)', zIndex: 2500, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div className="pathways-chooser-modal" style={{ background: 'linear-gradient(135deg, #0f1629d0, #1a2342d4)', padding: '40px 36px', borderRadius: '20px', width: 'min(96%, 700px)', color: 'white', textAlign: 'center', border: '1px solid rgba(84, 201, 255, 0.22)', boxShadow: '0 20px 80px rgba(0, 179, 255, 0.15)', position: 'relative' }}>
        <button onClick={onClose} className="pathways-chooser-close-btn" style={{ position: 'absolute', top: '16px', right: '20px', background: 'none', border: 'none', color: '#aaa', fontSize: '28px', cursor: 'pointer' }}>×</button>

        <div style={{ marginBottom: '28px' }}>
          <h2 style={{ color: '#00e1ff', fontSize: '1.9rem', margin: '0 0 8px' }}>Choose Your Pathway</h2>
          <p style={{ color: '#88aacc', fontSize: '0.95rem', margin: 0 }}>Select a structured journey to guide your academic and career development</p>
        </div>

        <div className="pathways-chooser-content" style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>

          {/* ASPIRELab */}
<button onClick={() => setSelectedPathway('aspirelab')} style={btn('54,167,248')} onMouseEnter={e=>hoverIn(e,'54,167,248')} onMouseLeave={e=>hoverOut(e,'54,167,248')}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '16px' }}>
              <div style={{
                width: '44px', height: '44px', borderRadius: '12px', flexShrink: 0,
                background: 'rgba(54,167,248,0.15)', border: '1px solid rgba(54,167,248,0.4)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <FaRocket style={{ fontSize: '1.3rem', color: '#36a7f8' }} />
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '6px' }}>
                  <strong style={{ fontSize: '1.25rem', color: '#36a7f8' }}>ASPIRELab™</strong>
                  <span className="pathway-step-count" style={{ padding: '2px 10px', borderRadius: '999px', fontSize: '0.75rem', background: 'rgba(54,167,248,0.15)', border: '1px solid #36a7f8', color: '#36a7f8' }}>2 Steps</span>
                  <a  href="https://www.oxbridgegateway.com/aspirelab" target="_blank" rel="noopener noreferrer" className="nav-item-pathway pathway-arrow">learn more ↗</a>
                </div>
                <p style={{ margin: '4px 0 0', color: '#a0d4ff', fontSize: '0.9rem' }}>Learn to think, build, and experiment like entrepreneurs</p>
                <p style={{ margin: 0, color: '#a0d4ff', fontWeight: '100', fontSize: '0.9rem', lineHeight: 1.5 }}>Innovate → Launch</p>
              </div>
              <span className='pathway-arrow' style={{ color: '#36a7f8', fontSize: '1.4rem', alignSelf: 'center' }}>→</span>
            </div>
          </button>

          {/* SCHOLARClub */}
          <button onClick={() => setSelectedPathway('scholarclub')} style={btn('255,170,0')} onMouseEnter={e=>hoverIn(e,'255,170,0')} onMouseLeave={e=>hoverOut(e,'255,170,0')}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '16px' }}>
                <div style={{
                width: '44px', height: '44px', borderRadius: '12px', flexShrink: 0,
                background: '#ffaa0027', border: '1px solid #ffaa0076',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <FaUsers style={{ fontSize: '1.3rem', color: '#ffaa00' }} />
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '6px' }}>
                  <strong style={{ fontSize: '1.25rem', color: '#ffaa00' }}>SCHOLARClub™</strong>
                  <span className="pathway-step-count" style={{ padding: '2px 10px', borderRadius: '999px', fontSize: '0.75rem', background: 'rgba(255,170,0,0.15)', border: '1px solid #ffaa00', color: '#ffaa00' }}>3 Steps</span>
                  <a  href="https://www.oxbridgegateway.com/scholarclub" target="_blank" rel="noopener noreferrer" className="nav-item-pathway-scholarclub pathway-arrow">learn more ↗</a>
                </div>
                <p style={{ margin: '4px 0 0', color: '#ffd38a', fontSize: '0.9rem' }}>Building Future Scholars Through Shared Academic Passion</p>
                <p style={{ margin: 0, color: '#ffd38a', fontWeight: '100', fontSize: '0.9rem', lineHeight: 1.5 }}>Discover → Explore → Deepen Your Passion</p>
              </div>
              <span className='pathway-arrow' style={{ color: '#ffaa00', fontSize: '1.4rem', alignSelf: 'center' }}>→</span>
            </div>
          </button>



          {/* CAREERReady */}
          <button onClick={() => setSelectedPathway('careerready')} style={btn('138,43,226')} onMouseEnter={e=>hoverIn(e,'138,43,226')} onMouseLeave={e=>hoverOut(e,'138,43,226')}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '16px' }}>
              <div style={{
                width: '44px', height: '44px', borderRadius: '12px', flexShrink: 0,
                background: '#b794f42a', border: '1px solid #b794f46a',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <FaBriefcase style={{ fontSize: '1.3rem', color: '#b794f4' }} />
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '6px' }}>
                  <strong style={{ fontSize: '1.25rem', color: '#b794f4' }}>CAREERReady™</strong>
                  <span className="pathway-step-count" style={{ padding: '2px 10px', borderRadius: '999px', fontSize: '0.75rem', background: 'rgba(138,43,226,0.15)', border: '1px solid #b794f4', color: '#b794f4' }}>5 Steps</span>
                </div>
                <p style={{ margin: '0 0 2px', color: '#d4b8ff', fontSize: '0.9rem', lineHeight: 1.5 }}>From Academic Discovery to Career Success</p>
                <p style={{ margin: 0, color: '#d4b8ff', fontWeight: '100', fontSize: '0.9rem', lineHeight: 1.5 }}>Discover → Explore → Stay Informed → Build Experience → Get Hired</p>
              </div>
              <span className='pathway-arrow' style={{ color: '#b794f4', fontSize: '1.4rem', alignSelf: 'center' }}>→</span>
            </div>
          </button>

          {/* LEADAI */}
          <button onClick={() => setSelectedPathway('leadai')} style={btn('0,229,200')} onMouseEnter={e=>hoverIn(e,'0,229,200')} onMouseLeave={e=>hoverOut(e,'0,229,200')}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '16px' }}>
              <div style={{
                width: '44px', height: '44px', borderRadius: '12px', flexShrink: 0,
                background: '#00e5c628', border: '1px solid #00e5c66b',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <FaBrain style={{ fontSize: '1.3rem', color: '##00e5c8' }} />
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '6px' }}>
                  <strong style={{ fontSize: '1.25rem', color: '#00e5c8' }}>LEADAI™</strong>
                  <span className="pathway-step-count" style={{ padding: '2px 10px', borderRadius: '999px', fontSize: '0.75rem', background: 'rgba(0,229,200,0.15)', border: '1px solid #00e5c8', color: '#00e5c8' }}>3 Steps</span>
                </div>
                <p style={{ margin: '0 0 2px', color: '#7fffe6', fontSize: '0.9rem', lineHeight: 1.5 }}>Building Human Advantage in the AI Era</p>
                <p style={{ margin: 0, color: '#7fffe6', fontWeight: '100', fontSize: '0.9rem', lineHeight: 1.5 }}>Discover → Assess → Develop Your Human Advantage</p>
              </div>
              <span className='pathway-arrow' style={{ color: '#00e5c8', fontSize: '1.4rem', alignSelf: 'center' }}>→</span>
            </div>
          </button>

        </div>
      </div>
    </div>
  );
}

// Hoisted out of PathwayDetailModal so the export engine can resolve step
// keys to their labels without duplicating this list.
export const PATHWAY_STEPS = {
  careerready: [
    { key: 'uniready',     label: 'UniReady' },
    { key: 'compass',      label: 'Compass' },
    { key: 'profeed',      label: 'ProFeed' },
    { key: 'portfoliox',   label: 'PortfolioX' },
    { key: 'interviewpro', label: 'InterviewPro' },
  ],
  leadai: [
    { key: 'uniready', label: 'UniReady' },
    { key: 'aiready',  label: 'AIReady' },
    { key: 'aixcel',   label: 'AIXcel' },
  ],
  aspirelab: [
    { key: 'aspirelab',      label: 'Xperience' },
    { key: 'aspirelab_plus', label: 'LaunchPad' },
  ],
  uniadmission: [
    { key: 'uniready',        label: 'UniReady' },
    { key: 'knowledge',       label: 'Knowledge' },
    { key: 'acadfeed',        label: 'AcadFeed' },
    { key: 'supercurricular', label: 'SuperCurricular' },
    { key: 'unistatement',    label: 'UniStatement' },
  ],
  scholarclub: [
    { key: 'discover',    label: 'UniReady' },
    { key: 'explore',     label: 'Knowledge' },
    { key: 'scholarclub', label: 'SuperCurricular' },
  ],
};

/* ─── Pathway-Specific Input Configurations ─────────────────────────────── */
export function getStepInputFields(pathway, stepKey) {
    const baseStyle = {
    width: '100%', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: '8px', color: 'white', padding: '10px', fontSize: '0.9rem', marginTop: '8px'
  };

  const configs = {
// ====================== ASPIRELab ======================
aspirelab: {
  aspirelab: [                    // Main 7-Week Sprint
    { 
      type: 'range', 
      label: 'Innovation Confidence', 
      field: 'confidenceGained', 
      max: 5, 
      emoji: '🚀' 
    },
    { 
      type: 'textarea', 
      label: 'Key Takeaways', 
      field: 'keyLearnings', 
      placeholder: 'What skills, insights, or entrepreneurial mindsets did you develop? How will you apply them in your next project?'
    }
  ],
  
  aspirelab_plus: [               // LaunchPad Phase
    { 
      type: 'range', 
      label: 'Venture Readiness', 
      field: 'ventureReadiness', 
      max: 5, 
      emoji: '🌱' 
    },
    { 
      type: 'textarea', 
      label: 'Venture Progress', 
      field: 'currentProgress', 
      placeholder: 'What have you achieved so far? What are your biggest challenges, and what is your next milestone?'
    }
  ]
},

// ====================== SCHOLARClub ======================
scholarclub: {
  discover: [
    { 
      type: 'range', 
      label: 'Subject Passion', 
      field: 'passionLevel', 
      max: 5, 
      emoji: '🔥' 
    },
    { 
      type: 'text', 
      label: 'Favourite Subject', 
      field: 'topSubject', 
      placeholder: 'e.g. Biology, Economics, Computer Science' 
    },
    { 
      type: 'textarea', 
      label: 'Why This Subject', 
      field: 'subjectInterest', 
      placeholder: 'What fascinates you about this subject? Which topics or real-world problems excite you most?'
    }
  ],
  
  explore: [
    { 
      type: 'range', 
      label: 'Subject Understanding', 
      field: 'understanding', 
      max: 5, 
      emoji: '📚' 
    },
    { 
      type: 'textarea', 
      label: 'Key Insights', 
      field: 'keyLearnings', 
      placeholder: 'What are the most important ideas, concepts, or discoveries you learned today?'
    }
  ],
  
  scholarclub: [
    { 
      type: 'range', 
      label: 'Contribution Impact', 
      field: 'contributionImpact', 
      max: 5, 
      emoji: '🌟' 
    },
    { 
      type: 'textarea', 
      label: 'Reflection', 
      field: 'reflection', 
      placeholder: 'What did you contribute, what did you learn, and what would you do differently next time?'
    }
  ]
},

    // ====================== UniAdmission ======================
    uniadmission: {
      uniready: [
        { type: 'range', label: 'Subject Clarity', field: 'clarity', max: 10, emoji: '🎯' },
        { type: 'textarea', label: 'Top 3 Reasons for Chosen Subject', field: 'reasons' },
      ],
      knowledge: [
        { type: 'text', label: 'Key Resources Completed', field: 'resources' },
        { type: 'textarea', label: 'New Knowledge Gained', field: 'gained' },
      ],
      acadfeed: [
        { type: 'textarea', label: 'Recent Articles / Papers', field: 'articles', placeholder: 'Title + one key takeaway each' },
      ],
      supercurricular: [
        { type: 'text', label: 'Activity Name', field: 'activity' },
        { type: 'textarea', label: 'What I Learned + Evidence', field: 'evidence' },
      ],
      unistatement: [
        { type: 'range', label: 'Personal Statement Progress', field: 'progress', max: 100, emoji: '📝' },
        { type: 'textarea', label: 'Draft Link / Key Paragraphs', field: 'draft' },
        { type: 'textarea', label: 'Mentor / Teacher Feedback', field: 'feedback' },
      ]
    },

    // ====================== CAREERReady ======================
      careerready: {
      uniready:   [
      {
        type:'textarea',
        label:'Academic Identity',
        field:'interests',
        placeholder:'Your specific academic interests? E.g., Biology: exploring genetics, biotechnology, and healthcare innovation.'
      }
      ],
      compass:    [
      {
        type:'textarea',
        label:'Early Career Goals',
        field:'targets',
        placeholder:'Which roles or industries are you most interested in exploring?'
      }
      ],
      profeed:    [
      {
        type:'textarea',
        label:'Industry Insights',
        field:'insights',
        placeholder:'What trends, technologies, or industry developments stood out to you?'
      }
      ],
      portfoliox: [
      {
        type:'text',
        label:'Latest Achievement',
        field:'items',
        placeholder:'e.g. Research project, competition, internship'
      },
      {
        type:'textarea',
        label:'Reflection',
        field:'link',
        placeholder:'What did you accomplish, and why does it matter?'
      }
      ],
      interviewpro:[
      {
        type:'range',
        label:'Interview Confidence',
        field:'confidence',
        max:10,
        emoji:'💼'
      },
      {
        type:'textarea',
        label:'Improvement Plan',
        field:'notes',
        placeholder:'Which interview questions challenged you most, and how will you improve?'
      }
      ],
    },

// ====================== LEADAI ======================
leadai: {
  uniready: [
  ],
  
  aiready: [
    { 
      type: 'range', 
      label: 'AI Readiness', 
      field: 'aiReadiness', 
      max: 5, 
      emoji: '✨' 
    },
    { 
      type: 'textarea', 
      label: 'Human Advantage', 
      field: 'humanGaps', 
      placeholder:'Where can your uniquely human strengths create the greatest impact? Describe the situations, skills, or opportunities where you perform best.'
    }
  ],
  
  aixcel: [
    { 
      type: 'range', 
      label: 'Human Skills Progress', 
      field: 'skillsProgress', 
      max: 5, 
      emoji: '🧠' 
    },
    { 
      type: 'text', 
      label: 'Next Growth Focus',
      field: 'skillsAndTools', 
      placeholder: 'Which human capability will you intentionally develop next?'    }
  ]
},
  };

  return configs[pathway]?.[stepKey] || [
    // fallback
    { type: 'textarea', label: 'Reflection', field: 'notes', placeholder: 'What did you accomplish? What are your next actions?' }
  ];
}

const PathwayDetailModal = React.memo(function PathwayDetailModal({
  selectedPathway, pathwayProgress, setPathwayProgress, 
  setSelectedPathway, onClose, onNavigate, onGoStep, onSwitchPathway 
}) {  
  const isAspireLab = selectedPathway === 'aspirelab';
  const isScholarClub = selectedPathway === 'scholarclub';
  const isUniAdmission = selectedPathway === 'uniadmission';
  const isCareer      = selectedPathway === 'careerready';
  const isLeadAI      = selectedPathway === 'leadai';

  const accentColor  = isAspireLab ? '#36a7f8' : isScholarClub ? '#ffaa00' : isCareer ? '#b794f4' : isLeadAI ? '#00e5c8' : '#f8fafc';
  const accentBg     = isAspireLab ? 'rgba(54,167,248,0.15)' : isScholarClub ? 'rgba(255,170,0,0.15)' : isCareer ? 'rgba(138,43,226,0.15)' : isLeadAI ? 'rgba(0,229,200,0.15)' : 'rgba(255,255,255,0.08)';
  const accentBorder = isAspireLab ? 'rgba(54,167,248,0.45)' : isScholarClub ? 'rgba(255,170,0,0.45)' : isCareer ? 'rgba(138,43,226,0.45)' : isLeadAI ? 'rgba(0,229,200,0.45)' : 'rgba(255,255,255,0.28)';
  const accentGrad   = isAspireLab ? '#36a7f8' : isScholarClub ? '#ffcc66' : isCareer ? '#c4a1ff' : isLeadAI ? '#00e5c8' : '#d8dee9';

  const currentPathwayData = pathwayProgress[selectedPathway] || { completed: new Set(), goals: '', reflections: {} };
  const completedSet = currentPathwayData.completed || new Set();
  const reflections = currentPathwayData.reflections || {};
  
  const rawSteps = isCareer ? [
    { key: 'uniready',    tagline: 'Confirm your higher education direction',              description: 'Take the UniReady assessment to find your academic passions, or simply choose your top subject.', path: '/dashboard' },
    { key: 'compass',     tagline: 'Explore career pathways and choose your entry point',        description: 'Use Compass to explore career paths aligned with your top higher education subject.',                           path: '/CareerDashboard', state: { directToCompass: true } },
    { key: 'profeed',     tagline: 'Stay informed with real-time industry intelligence',         description: 'Follow curated industry news matched to your career direction.',                                           path: '/CareerDashboard', state: { sidebarItem: 'careerfeed' } },
    { key: 'portfoliox',  tagline: 'Build recruiter-valued experiences strategically',           description: 'Curate your academic and extracurricular achievements into a compelling portfolio.',                          path: '/CareerDashboard', state: { sidebarItem: 'portfoliox' } },
    { key: 'interviewpro',tagline: 'Prepare for interviews with AI-assisted guidance',           description: 'Practice and refine your interview technique with structured, AI-powered preparation.',                       path: '/CareerDashboard', state: { sidebarItem: 'interviewx' } },
  ] : isLeadAI ? [
    { key: 'uniready',tagline: 'Confirm your higher education subject',           description: 'Choose Your Top Subject on the UniReady page to establish your academic foundation and subject focus for the AI era.',                    path: '/dashboard' },
    { key: 'aiready', tagline: 'Identify your strengths and readiness for the AI era',      description: 'Assess your current AI readiness and identify the human capabilities you need to develop.',                   path: '/LeaderDashboard' },
    { key: 'aixcel',  tagline: 'Develop the essential human capabilities AI cannot replace', description: 'Follow a personalised development plan to strengthen creativity, critical thinking, emotional intelligence, and leadership.', path: '/LeaderDashboard', state: { sidebarItem: 'aixcel' } },
  ] : isAspireLab ? [
    { key: 'aspirelab',      tagline: '7-Week Innovation & Startup Sprint',   description: 'Join the hands-on entrepreneurship programme. Solve real-world problems through teamwork, experimentation, AI tools, and rapid execution. Complete startup sprint tasks from the ASPIREdeck Xperience page.', path: '/startup-xperience' },
    { key: 'aspirelab_plus', tagline: 'From Idea to Real Venture',            description: 'Transform promising ideas into structured startups using the LaunchPad framework.', path: '/LeaderDashboard', state: { sidebarItem: 'launchpad' } },
  ] : isUniAdmission ? [ // NEW: UniAdmission steps
    { key: 'uniready',      tagline: 'Discover your academic direction',           description: 'Complete the UniReady psychometric assessment to identify your strongest academic interests and subject passions.', path: '/dashboard' },
    { key: 'knowledge',     tagline: 'Build your subject foundation',              description: 'Explore introductory content, emerging topics, and university-level concepts to deepen your understanding.', path: '/dashboard_enrich', state: { sidebarItem: 'knowledge' } },
    { key: 'acadfeed',      tagline: 'Stay current with academic insights',       description: 'Follow curated academic news and research developments matched to your subject interests.', path: '/dashboard_enrich', path:  '/enrichfeed'  },
    { key: 'supercurricular', tagline: 'Demonstrate genuine passion',           description: 'Engage in meaningful supercurricular activities that showcase your intellectual curiosity and commitment to your subject.', path: '/dashboard_enrich', state: { sidebarItem: 'supercurricular' } },
    { key: 'unistatement',  tagline: 'Craft your compelling university application', description: 'Write a powerful personal statement that effectively communicates your academic journey, passion, and potential.', path: '/dashboard_enrich', state: { sidebarItem: 'personalstatement' }},
  ] : [
   { key: 'discover',   tagline: 'Discover your ideal higher education direction', description: 'Complete the UniReady psychometric assessment to identify your strongest academic interests and subject passions.', path: '/dashboard' },
    { key: 'explore',    tagline: 'Explore Your Subject',                          description: 'Explore introductory content, emerging topics, and university-level concepts.',                                   path: '/dashboard_enrich', state: { sidebarItem: 'knowledge' } },
    { key: 'scholarclub',tagline: 'Learn, Build & Grow Together',                 description: 'Collaborate with like-minded students, complete meaningful supercurricular activities, share discoveries, develop practical knowledge and build genuine passion.', path: '/dashboard_enrich', state: { sidebarItem: 'supercurricular' } },
  ];

  // Labels are sourced from PATHWAY_STEPS (the same constant the export
  // engine's summarizePathwayProgress reads) so there is one canonical
  // key→label mapping instead of two hand-maintained copies.
  const stepLabelByKey = Object.fromEntries(
    (PATHWAY_STEPS[selectedPathway] || []).map(s => [s.key, s.label])
  );
  const steps = rawSteps.map(s => ({ ...s, label: stepLabelByKey[s.key] || s.label }));

const nextIncompleteIndex = steps.findIndex(s => !completedSet.has(s.key));
  const completedCount = steps.filter(s => completedSet.has(s.key)).length;
  const progressPct = Math.round((completedCount / steps.length) * 100);

  const [hideCompleted, setHideCompleted] = useState(false);
  const nextStepRef = useRef(null);
  const scrollToNext = useCallback(() => {
    nextStepRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, []);

const updateGoals = (newGoals) => {
  setPathwayProgress(prev => ({
    ...prev,
    [selectedPathway]: {
      // Ensure we don't lose existing data if the pathway entry is missing
      ...(prev[selectedPathway] || { 
        completed: new Set(), 
        reflections: {} 
      }),
      goals: newGoals
    }
  }));
};

  const updateReflection = (stepKey, field, value) => {
    setPathwayProgress(prev => ({
      ...prev,
      [selectedPathway]: {
        ...prev[selectedPathway],
        reflections: {
          ...(prev[selectedPathway]?.reflections || {}),
          [stepKey]: { ...(prev[selectedPathway]?.reflections?.[stepKey] || {}), [field]: value }
        }
      }
    }));
  };

  const toggleStep = (key) => {
    setPathwayProgress(prev => {
const existing = prev[selectedPathway] || { completed: new Set(), goals: '', reflections: {} };
      const newSet = new Set(existing.completed);
      if (newSet.has(key)) newSet.delete(key); else newSet.add(key);
return { ...prev, [selectedPathway]: { ...existing, completed: newSet } };
    });
  };

  const pathwayGoalPlaceholders = {
  scholarclub:
    "What are your goals for this pathway? E.g. Deepen your Physics knowledge and complete three supercurricular projects before university.",

  uniadmission:
    "What is your university admission goal? E.g. Build a competitive application for Medicine at the University of Melbourne.",

  careerready:
    "What career outcome are you working towards? E.g. Secure a software engineering internship and build a portfolio that stands out to employers.",

  leadai:
    "Which human skills will you develop for the AI era? E.g. Strengthen leadership, creativity, and critical thinking while collaborating with AI.",

  aspirelab:
    "What do you want to achieve through this startup journey? E.g. Validate my idea, build a prototype, and confidently pitch it to potential investors."
};

  return (
    <div className="modal" style={{ position: 'fixed', inset: 0, background: 'rgba(0, 0, 0, 0.83)', zIndex: 2600, display: 'flex', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(6px)' }}>
<div style={{ background: 'linear-gradient(160deg, #0a10205f, #11182762)', borderRadius: '20px', width: 'min(95%, 640px)', maxHeight: '94vh', overflowY: 'auto', color: 'white', border: `1px solid ${accentBorder}`, boxShadow: `0 24px 90px rgba(0,0,0,0.7), 0 0 60px ${accentBg}`, position: 'relative', scrollbarWidth: 'none', msOverflowStyle: 'none' }} 
className="custom-scrollbar pathway-modal-content">
        {/* Header */}
        <div className="pathway-modal" style={{ padding: '28px 32px 0', borderBottom: `1px solid ${accentBorder}`, marginBottom: '8px', position: 'sticky', top: 0, background: 'linear-gradient(160deg, #0a1020, #111827)', zIndex: 10 }}>
          <button onClick={onClose} className="pathway-detail-close-btn" style={{ position: 'absolute', top: '16px', right: '20px', background: 'none', border: 'none', color: '#888', fontSize: '26px', cursor: 'pointer' }}>×</button>
          <div className="pathway-modal-header" style={{ display: 'flex', alignItems: 'center', gap: '14px', marginBottom: '12px' }}>
            <div>
              <h2 style={{ margin: 0, color: accentColor, fontSize: '1.75rem', fontWeight: 700 }}>
                {isScholarClub ? 'SCHOLARClub™' : isAspireLab ? 'ASPIRELab™' : isCareer ? 'CAREERReady™' : isLeadAI ? 'LEADAI™' : 'UniAdmission™'}
              </h2>
              <p style={{ margin: '4px 0 0', color: '#88aacc' }}>
                {isScholarClub ? 'Creating subject-based academic communities that help students become stronger university applicants through sustained supercurricular engagement and shared learning'
                 : isAspireLab  ? 'Combining human creativity with AI assisted workflows, students move beyond passive learning to develop real ideas through collaboration, rapid iteration, and hands on execution'
                 : isCareer     ? 'Turning Academic Potential Into Career Success'
                 : isLeadAI     ? 'Identify, strengthen, and continuously develop the critical human skills needed for future leadership, innovation, and adaptability'
                 : 'Develop the knowledge, curiosity, and academic profile that leading universities look for. Build genuine subject expertise, stay informed with the latest developments, demonstrate meaningful supercurricular engagement, and craft an application that reflects your true potential.'}
              </p>
            </div>
          </div>

{/* Progress bar */}
          <div style={{ marginBottom: '16px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
              <span style={{ fontSize: '0.85rem', color: '#aaa' }}>{completedCount} of {steps.length} steps completed</span>
              <span style={{ fontSize: '0.85rem', fontWeight: 700, color: accentColor }}>{progressPct}%</span>
            </div>
            <div style={{ height: '8px', background: 'rgba(255,255,255,0.08)', borderRadius: '99px', overflow: 'hidden' }}>
              <div style={{ height: '100%', width: `${progressPct}%`, background: `linear-gradient(90deg, ${accentColor}, ${accentGrad})`, borderRadius: '99px', transition: 'width 0.5s ease' }} />
            </div>
          </div>

          {completedCount > 0 && completedCount < steps.length && (
            <div style={{ display: 'flex', gap: '10px', marginBottom: '20px', flexWrap: 'wrap' }}>
              <button
                onClick={scrollToNext}
                style={{
                  padding: '6px 14px', borderRadius: '999px', fontSize: '0.8rem', fontWeight: 600,
                  background: accentBg, border: `1px solid ${accentBorder}`, color: accentColor, cursor: 'pointer',
                }}
              >
                ↓ Jump to next step
              </button>
              <button
                onClick={() => setHideCompleted(h => !h)}
                style={{
                  padding: '6px 14px', borderRadius: '999px', fontSize: '0.8rem', fontWeight: 600,
                  background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.15)',
                  color: '#aaa', cursor: 'pointer',
                }}
              >
                {hideCompleted ? 'Show completed steps' : 'Hide completed steps'}
              </button>
            </div>
          )}
        </div>

       {/* === NEW: Personal Goals Section === */}
                <div style={{ padding: '0 32px 20px', borderBottom: `1px solid ${accentBorder}` }}>
          <label style={{ fontSize: '0.9rem', color: '#88aacc', display: 'block', marginBottom: '6px' }}>
            My Goal for This Pathway
          </label>
          <textarea
          className='space-scroll'
  value={currentPathwayData.goals || ''}
  onChange={(e) => updateGoals(e.target.value)}
    placeholder={pathwayGoalPlaceholders[selectedPathway] || "Describe what you hope to achieve through this pathway..."}
            style={{
              width: '100%', minHeight: '68px', background: 'rgba(255,255,255,0.06)',
              border: `1px solid ${accentBorder}`, borderRadius: '10px',
              color: 'white', padding: '12px', fontSize: '0.95rem', resize: 'vertical'
            }}
          />
        </div>


        {/* Steps */}
        <div className="pathway-steps" style={{ padding: '24px 32px 32px' }}>
               <div className="all-answers-saved-note" style={{fontStyle: 'normal', marginTop: '0px',  marginBottom: '0px', textAlign: 'center', color: '#b6b6b6', fontSize: '14px' }}>
                          <p>Your Pathway Notes are saved locally in this browser only.</p>
                        </div>
          <style>{`@keyframes pulseRing { 0%{box-shadow:0 0 0 0 ${accentColor}bb,0 0 0 0 ${accentColor}55} 60%{box-shadow:0 0 0 10px ${accentColor}22,0 0 0 22px ${accentColor}08} 100%{box-shadow:0 0 0 0 ${accentColor}00,0 0 0 0 ${accentColor}00} }`}</style>

         {steps.map((step, idx) => {
            const isCompleted = completedSet.has(step.key);
            const isNext = idx === steps.findIndex(s => !completedSet.has(s.key));
            const isLast      = idx === steps.length - 1;
            const stepReflection = reflections[step.key] || {};

            if (hideCompleted && isCompleted) return null;

            return (
              <div key={step.key} ref={isNext ? nextStepRef : null} style={{ display: 'flex', gap: '0', position: 'relative' }}>                                {/* Circle + connector */}
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '56px', flexShrink: 0 }}>
                                 <button
                    onClick={() => toggleStep(step.key)}
                    title={isCompleted ? 'Click to mark incomplete' : 'Click to mark complete'}
                    style={{
                      width: '46px', height: '46px', borderRadius: '50%', flexShrink: 0,
                      background: isCompleted ? `linear-gradient(135deg, ${accentColor}, ${accentGrad})` : isNext ? 'rgba(30,40,60,0.9)' : 'rgba(20,28,50,0.8)',
                      border: isCompleted ? `2px solid ${accentColor}` : isNext ? `2.5px solid ${accentColor}` : '2px solid rgba(100,120,160,0.35)',
                      color: isCompleted ? '#fff' : isNext ? accentColor : '#555',
                      fontSize: isCompleted ? '1.3rem' : '1.05rem', fontWeight: 'bold', cursor: 'pointer',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      transition: 'all 0.3s ease', zIndex: 2,
                      animation: isNext ? 'pulseRing 1.8s ease-out infinite' : 'none',
                    }}
                    onMouseEnter={e => { e.currentTarget.style.transform = 'scale(1.1)'; }}
                    onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; }}
                  >
                    {isCompleted ? '✓' : isNext ? (step.emoji || '→') : <span style={{ opacity: 0.4 }}>{idx + 1}</span>}
                  </button>
                  {!isLast && (
                    <div style={{ width: '3px', flex: 1, minHeight: '40px', background: isCompleted ? `linear-gradient(180deg, ${accentColor}, ${accentColor}88)` : 'rgba(100,120,160,0.18)', borderRadius: '99px', marginTop: '4px', transition: 'background 0.4s ease' }} />
                  )}
                </div>

                {/* Step content */}
                <div className="step-content" style={{ flex: 1, paddingLeft: '20px', paddingBottom: isLast ? '0' : '32px', paddingTop: '2px' }}>
                  <div style={{ background: isCompleted ? accentBg : isNext ? 'rgba(30,42,70,0.6)' : 'rgba(18,26,46,0.5)', border: `1px solid ${isCompleted ? accentBorder : isNext ? accentBorder : 'rgba(80,100,140,0.2)'}`, borderRadius: '14px', padding: '18px 20px', transition: 'all 0.3s ease', opacity: (!isCompleted && !isNext && idx > nextIncompleteIndex) ? 0.55 : 1 }}>
                    <div className="step-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <span style={{ fontSize: '1.15rem', fontWeight: 700, color: isCompleted ? accentColor : isNext ? '#fff' : '#778899' }}>{step.label}</span>
                        {isNext      && <span style={{ padding: '2px 10px', borderRadius: '999px', fontSize: '0.72rem', background: `${accentColor}22`, border: `1px solid ${accentColor}`, color: accentColor, fontWeight: 600 }}>Next Step</span>}
                        {isCompleted && <span style={{ padding: '2px 10px', borderRadius: '999px', fontSize: '0.72rem', background: 'rgba(0,200,100,0.15)', border: '1px solid rgba(0,200,100,0.5)', color: '#4ade80', fontWeight: 600 }}>Completed</span>}
                      </div>
                                        <button
className="pathway-step-go-btn"
onClick={() => { if (step.path) onGoStep(step.path, step.state || {}, selectedPathway); }}                        
style={{
  padding: '7px 18px', 
  background: isCompleted ? 'rgba(0,200,100,0.15)' : isNext ? accentColor : 'rgba(80,100,140,0.2)', 
  border: `1px solid ${isCompleted ? 'rgba(0,200,100,0.5)' : isNext ? accentColor : 'rgba(80,100,140,0.3)'}`, 
  borderRadius: '8px', color: isCompleted ? '#4ade80' : isNext ? '#fff' : '#556', 
  fontWeight: 600, 
  color: '#dedede',
  fontSize: '0.85rem', 
  cursor: step.path ? 'pointer' : 'not-allowed', whiteSpace: 'nowrap', flexShrink: 0 
}}
                        onMouseEnter={e => { if (step.path) e.currentTarget.style.filter = 'brightness(1.05)'; }}
                        onMouseLeave={e => { e.currentTarget.style.filter = 'brightness(1)'; }}
                      >
                        {isCompleted ? 'Revisit' : 'Go →'}
                      </button>
                    </div>
                    <p style={{ margin: '0 0 6px', fontSize: '0.88rem', fontWeight: 600, color: isCompleted ? `${accentColor}bb` : isNext ? '#a0c4e8' : '#556677' }}>{step.tagline}</p>
                    {(isNext || isCompleted) && <p style={{ margin: 0, fontSize: '0.85rem', color: '#778899', lineHeight: 1.55 }}>{step.description}</p>}

 {/* === Dynamic Pathway-Specific Inputs === */}
{(() => {
  const fields = getStepInputFields(selectedPathway, step.key);
  if (fields.length === 0) return null;   // ← This hides the box completely for uniready in LeadAI

  return (
       <div className="pathway-step-fields" style={{ 
      marginTop: '18px', 
      padding: '16px', 
      background: 'rgba(0,0,0,0.25)', 
      borderRadius: '12px', 
      border: `1px solid rgba(255,255,255,0.1)` 
    }}>
      {fields.map((fieldConfig, i) => {
        const value = stepReflection[fieldConfig.field] || (fieldConfig.type === 'range' ? 5 : '');

        const inputStyle = {
          width: '100%', 
          background: 'rgba(255,255,255,0.05)', 
          border: '1px solid rgba(255,255,255,0.1)',
          borderRadius: '8px', 
          color: 'white', 
          padding: '10px', 
          fontSize: '0.9rem',
          marginTop: '6px'
        };

        return (
          <div key={i} style={{ marginBottom: i < fields.length - 1 ? '16px' : 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px', fontSize: '0.85rem', color: '#a0c4e8' }}>
              {fieldConfig.emoji && <span>{fieldConfig.emoji}</span>}
              <strong>{fieldConfig.label}</strong>
            </div>

            {fieldConfig.type === 'range' ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <input
                  type="range"
                  min="1"
                  max={fieldConfig.max || 10}
                  value={value}
                  onChange={(e) => updateReflection(step.key, fieldConfig.field, parseInt(e.target.value))}
                  style={{ flex: 1 }}
                />
                <span style={{ fontWeight: 700, minWidth: '38px', textAlign: 'right' }}>
                  {value}/{fieldConfig.max || 10}
                </span>
              </div>
            ) : fieldConfig.type === 'textarea' ? (
              <textarea
                        className='space-scroll'
                value={value}
                onChange={(e) => updateReflection(step.key, fieldConfig.field, e.target.value)}
                placeholder={fieldConfig.placeholder}
                style={{ ...inputStyle, minHeight: '68px', resize: 'vertical' }}
              />
            ) : (
              <input
                type="text"
                value={value}
                onChange={(e) => updateReflection(step.key, fieldConfig.field, e.target.value)}
                placeholder={fieldConfig.placeholder}
                style={inputStyle}
              />
            )}
          </div>
        );
      })}
    </div>
  );
})()}
                  </div>
                </div>
              </div>
            );
          })}

        {completedCount === steps.length && (
  <div style={{ marginTop: '25px', textAlign: 'center' }}>
    <FaTrophy style={{
      fontSize: '2.4rem',
      color: '#f9da13',
      filter: 'drop-shadow(0 0 12px #f9da137f)',
      marginBottom: '10px',
    }} />
    <p style={{ margin: 0, fontWeight: 700, fontSize: '1.1rem', color: '#f9da13' }}>Pathway Complete!</p>
    <p style={{ margin: '6px 0 0', color: '#aaa', fontSize: '0.9rem' }}>
      {isAspireLab ? "You've completed all ASPIRELab steps. You're an entrepreneur!" : isCareer ? "You've completed all CAREERReady steps. You're career-ready!" : isUniAdmission ? "You've completed all UniAdmission steps. All the best with your university application!" : "You've completed all steps!"}
    </p>
  </div>
)}

              <div className="pathway-modal-footer" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '28px', paddingTop: '20px', borderTop: '1px solid rgba(255,255,255,0.08)' }}>
            <button onClick={onSwitchPathway} style={{ background: 'none', border: '1px solid rgba(120,140,180,0.35)', color: '#778', borderRadius: '8px', padding: '9px 18px', cursor: 'pointer', fontSize: '0.88rem' }}>
              ← Switch Pathway
            </button>
            <button onClick={onClose} style={{ background: accentBg, border: `1px solid ${accentBorder}`, color: accentColor, borderRadius: '8px', padding: '9px 20px', cursor: 'pointer', fontSize: '0.88rem', fontWeight: 600 }}>
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
});

          {/* UniAdmission */}
         /*<button onClick={() => setSelectedPathway('uniadmission')} style={btn('255,255,255')} onMouseEnter={e=>hoverIn(e,'255,255,200')} onMouseLeave={e=>hoverOut(e,'255,255,200')}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '16px' }}>
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '6px' }}>
                  <strong style={{ fontSize: '1.25rem', color: '#ffffff' }}>UniAdmission™</strong>
                  <span className="pathway-step-count" style={{ padding: '2px 10px', borderRadius: '999px', fontSize: '0.75rem', background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.35)', color: '#ffffff' }}>5 Steps</span>
                </div>
                <p style={{ margin: '0 0 2px', color: '#d7dde7', fontSize: '0.9rem', lineHeight: 1.5 }}>Transform Academic Potential into University Offers</p>
                <p style={{ margin: 0, color: '#d7dde7', fontWeight: '100', fontSize: '0.9rem', lineHeight: 1.5 }}>Discover → Explore → Stay Current → Stand Out → Apply</p>
              </div>
              <span style={{ color: '#ffffff', fontSize: '1.4rem', alignSelf: 'center' }}>→</span>
            </div>
          </button>*/